import { inject, Injectable, NgZone } from '@angular/core';
import { HttpTransportType, HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { Observable, Subject } from 'rxjs';
import { CustomWindow } from '../../@types';

declare let window: CustomWindow;

@Injectable()
export class SignalrService {
    private readonly ngZone = inject(NgZone);

    private hubConnection: any;

    private url: string = 'ChartHub';
    private chartHubGroupRegister: string = 'RegisterForChatGroup';
    private chartHubGroupName: string = 'UpdateChatGroup';
    private updateDeliveryOrdersName: string = 'UpdateDeliveryOrders';
    private updateRidersName: string = 'updateRiders';
    private updateOnboarding: string = 'updateOnboarding';
    private updateOnboardingMerchants: string = 'updateOnboardingMerchants';
    private updateRidersLiveMap: string = 'updateRidersLiveMap';
    private fraudAlertCreated: string = 'fraudAlertCreated';
    private driverApplicationEditLocked: string = 'driverApplicationEditLocked';

    private $groupChat: Subject<any> = new Subject<any>();
    private $deliveryOrders: Subject<any> = new Subject<any>();
    private $riders: Subject<any> = new Subject<any>();
    private $onboarding: Subject<any> = new Subject<any>();
    private $onboardingMerchants: Subject<any> = new Subject<any>();
    private $ridersLiveMap: Subject<any> = new Subject<any>();
    private $fraudAlert: Subject<any> = new Subject<any>();
    private $driverEditLock: Subject<any> = new Subject<any>();

    public get GroupChatObservable(): Observable<any> {
        return this.$groupChat.asObservable();
    }

    public get DeliveryOrdersObservable(): Observable<any> {
        return this.$deliveryOrders.asObservable();
    }

    public get DriverEditLockObservable(): Observable<any> {
        return this.$driverEditLock.asObservable();
    }

    public get RidersObservable(): Observable<any> {
        return this.$riders.asObservable();
    }

    public get OnboardingObservable(): Observable<any> {
        return this.$onboarding.asObservable();
    }

    public get OnboardingMerchantsObservable(): Observable<any> {
        return this.$onboardingMerchants.asObservable();
    }

    public get UpdateRidersLiveMapObservable(): Observable<any> {
        return this.$ridersLiveMap.asObservable();
    }

    public get FraudAlertObservable(): Observable<any> {
        return this.$fraudAlert.asObservable();
    }

    constructor() {
    }

    public startConnection() {
        return new Promise((resolve, reject) => {
            const hub = this.hubConnection as HubConnection | undefined;
            if (hub?.state === 'Connected') {
                return resolve(true);
            }

            const fullUrl = `${window['apiUrl']}${this.url}`;
            console.log('Signalr connection url: ' + fullUrl);

            if (!this.hubConnection) {
                this.hubConnection = new HubConnectionBuilder()
                    .withUrl(fullUrl, {
                        skipNegotiation: true,
                        transport: HttpTransportType.WebSockets
                    })
                    .configureLogging(LogLevel.Warning)
                    .withAutomaticReconnect() //will automatically try to reconnect
                    .build();
                this.hubConnection.serverTimeoutInMilliseconds = 1000 * 60 * 10;
            }

            (this.hubConnection as HubConnection)
                .start()
                .then(() => {
                    console.log('connection established');
                    return resolve(true);
                })
                .catch((err: any) => {
                    console.log('error occured' + err);
                    reject(err);
                });
        });
    }

    public joinGroupChat(groupName: string) {
        return new Promise((resolve, reject) => {
            (this.hubConnection as HubConnection)
                .invoke(this.chartHubGroupRegister, groupName)
                .then(() => {
                    console.log('added to chat');
                    return resolve(true);
                }, (err: any) => {
                    console.log(err);
                    return reject(err);
                });
        });
    }

    public listenToGroupChat() {
        (this.hubConnection as HubConnection).on(this.chartHubGroupName, (data: any) => {
            this.ngZone.run(() => this.$groupChat.next(data));
        });
    }

    public addUpdateDeliveryOrdersListener = () => {
        (this.hubConnection as HubConnection).on(this.updateDeliveryOrdersName, (data: any) => {
            this.ngZone.run(() => this.$deliveryOrders.next(data));
        });
    };

    public removeUpdateDeliveryOrdersListener = () => {
        (this.hubConnection as HubConnection)?.off(this.updateDeliveryOrdersName);
    };

    public addUpdateRidersListener = () => {
        (this.hubConnection as HubConnection).on(this.updateRidersName, (data: any) => {
            this.ngZone.run(() => this.$riders.next(data));
        });
    };

    public driverApplicationEditListener = () => {
        (this.hubConnection as HubConnection).on(this.driverApplicationEditLocked, (data: any) => {
            this.ngZone.run(() => this.$riders.next(data));
        });
    };

    public removeUpdateRidersListener = () => {
        (this.hubConnection as HubConnection)?.off(this.updateRidersName);
    };

    public addOnboardingListener = () => {
        (this.hubConnection as HubConnection).on(this.updateOnboarding, (data: any) => {
            this.ngZone.run(() => this.$onboarding.next(data));
        });
    };

    public removeOnboardingListener = () => {
        (this.hubConnection as HubConnection)?.off(this.updateOnboarding);
    };

    public addOnboardingMerchantsListener = () => {
        (this.hubConnection as HubConnection).on(this.updateOnboardingMerchants, (data: any) => {
            this.ngZone.run(() => this.$onboardingMerchants.next(data));
        });
    };

    public removeOnboardingMerchantsListener = () => {
        (this.hubConnection as HubConnection)?.off(this.updateOnboardingMerchants);
    };

    public updateRidersLiveMapListener = () => {
        const hub = this.hubConnection as HubConnection | undefined;
        if (!hub) {
            return;
        }
        hub.off(this.updateRidersLiveMap);
        hub.on(this.updateRidersLiveMap, (data: any) => {
            this.ngZone.run(() => this.$ridersLiveMap.next(data));
        });
    };

    public removeRidersLiveMapListener = () => {
        (this.hubConnection as HubConnection)?.off(this.updateRidersLiveMap);
    };

    public addFraudAlertListener = () => {
        const hub = this.hubConnection as HubConnection | undefined;
        if (!hub) {
            return;
        }
        hub.off(this.fraudAlertCreated);
        hub.on(this.fraudAlertCreated, (data: any) => {
            this.ngZone.run(() => this.$fraudAlert.next(data));
        });
    };

    public removeFraudAlertListener = () => {
        (this.hubConnection as HubConnection)?.off(this.fraudAlertCreated);
    };

    public disconnect(): void {
        this.hubConnection?.stop();
    }
}
